import { useEffect, useId, useRef, useState } from "react";

/**
 * Magic UI（https://magicui.design）から移植した装飾。
 *
 * ★ **Tailwind と motion は入れない。**
 *
 * 本家は Tailwind のユーティリティと `motion/react` を前提にしている。
 * この画面は手書きの CSS トークン 1 枚で組んであり、
 *  - Tailwind の preflight は既存のレイアウトと衝突する
 *  - `motion` は 90KB の束に 50KB 級を足す
 * ので、**効果だけを CSS に移し替える**。
 * 元の実装が CSS で完結しているもの（ShineBorder / ShinyText）は
 * 手法をそのまま写し、motion に依存しているもの（BorderBeam /
 * NumberTicker / BlurFade）は CSS アニメーションと rAF で置き換えた。
 *
 * ★ 使う場所を絞る。この基盤の指針は「装飾よりも可読性と信頼感」で、
 * アニメーションは控えめにする。動かすのは**状態を伝えるとき**
 * （実行中・読み込み中・数値の更新）と、**入口**（ログイン画面）だけ。
 *
 * ★ すべて `prefers-reduced-motion` で止まる。指針の Accessibility に従う。
 */

/**
 * 枠を光が回る。
 *
 * 本家（shine-border）と同じ手法: 角丸を継いだ箱に放射グラデーションを敷き、
 * `mask-composite: exclude` で**内側をくり抜いて枠だけ**残す。
 * 動きは `background-position` のアニメーション。
 */
export function ShineBorder({
  borderWidth = 1,
  duration = 14,
  color = "var(--link)",
}: {
  borderWidth?: number;
  duration?: number;
  /** 単色でも配列でもよい。配列は虹のように繋がる。 */
  color?: string | readonly string[];
}): React.JSX.Element {
  const shine = Array.isArray(color) ? color.join(",") : (color as string);
  return (
    <div
      aria-hidden="true"
      className="shine-border"
      style={{
        // 透明 → 色 → 透明。位置を動かすと、光が枠を巡って見える。
        backgroundImage: `radial-gradient(transparent, transparent, ${shine}, transparent, transparent)`,
        padding: `${borderWidth}px`,
        animationDuration: `${duration}s`,
      }}
    />
  );
}

/**
 * 枠の上を光点が走る。
 *
 * 本家（border-beam）は motion で `offsetDistance` を動かす。
 * ここは CSS の `offset-path` と `@keyframes` で同じことをする。
 *
 * ★ 「いま動いている」を伝えるためだけに使う。飾りとして常時出さない。
 */
export function BorderBeam({
  size = 60,
  duration = 6,
  colorFrom = "var(--link)",
  colorTo = "var(--primary)",
  radius = "var(--radius)",
}: {
  size?: number;
  duration?: number;
  colorFrom?: string;
  colorTo?: string;
  /** なぞる枠の角丸。丸い錠剤なら 999px を渡す。 */
  radius?: string;
}): React.JSX.Element {
  return (
    <div aria-hidden="true" className="border-beam">
      <div
        className="border-beam-dot"
        style={{
          width: size,
          height: size,
          animationDuration: `${duration}s`,
          // ★ 角丸を合わせないと、光点が角で枠から外れる。
          offsetPath: `rect(0 auto auto 0 round ${radius})`,
          backgroundImage: `linear-gradient(to left, ${colorFrom}, ${colorTo}, transparent)`,
        }}
      />
    </div>
  );
}

/**
 * 数を数え上げる。
 *
 * 本家（number-ticker）は motion のばね（damping 60 / stiffness 100）を使う。
 * ここは同じ係数のばねを `requestAnimationFrame` で積む。
 *
 * ★ **桁を固定する**（`tabular-nums`）。数字の幅が揺れると、
 * 動いている間ずっと横にずれて読みにくい。
 */
export function NumberTicker({
  value,
  decimals = 0,
  prefix = "",
  suffix = "",
}: {
  value: number;
  decimals?: number;
  prefix?: string;
  suffix?: string;
}): React.JSX.Element {
  const [shown, setShown] = useState(value);
  const from = useRef(value);
  const raf = useRef(0);

  useEffect(() => {
    if (prefersReducedMotion()) {
      setShown(value);
      from.current = value;
      return undefined;
    }

    // ばねの積分。motion の既定（damping 60 / stiffness 100 / mass 1）に合わせる。
    let position = from.current;
    let velocity = 0;
    let last = performance.now();

    const step = (now: number): void => {
      // 1 フレームの刻みは 64ms で頭打ちにする。タブが戻った直後に
      // 大きな dt が来ると、ばねが発散して数字が飛ぶ。
      const dt = Math.min((now - last) / 1000, 0.064);
      last = now;
      const accel = 100 * (value - position) - 60 * velocity;
      velocity += accel * dt;
      position += velocity * dt;

      if (Math.abs(value - position) < 0.5 / 10 ** decimals && Math.abs(velocity) < 0.5) {
        from.current = value;
        setShown(value);
        return;
      }
      setShown(position);
      raf.current = requestAnimationFrame(step);
    };

    raf.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf.current);
  }, [value, decimals]);

  return (
    <span className="ticker">
      {prefix}
      {shown.toLocaleString("ja-JP", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      })}
      {suffix}
    </span>
  );
}

/**
 * 文字の上を光が流れる。
 *
 * 本家（animated-shiny-text）と同じ手法: 文字で切り抜いた背景
 * （`background-clip: text`）の位置を動かす。
 *
 * ★ 「待っている」ことを伝える用途に限る。見出しに使うと、
 * 読ませたい文字が常に animate していて疲れる。
 */
export function ShinyText({ children }: { children: React.ReactNode }): React.JSX.Element {
  return <span className="shiny-text">{children}</span>;
}

/**
 * 読み込み中の枠。
 *
 * ★ 「読み込み中…」の一行より、**入るものの形**を出す方が待ちやすい。
 * 光沢は ShinyText と同じ流れ方に揃えてある。
 */
export function Skeleton({
  rows = 3,
  height = 16,
}: {
  rows?: number;
  height?: number;
}): React.JSX.Element {
  return (
    <div className="skeleton-stack" aria-busy="true" aria-live="polite">
      <span className="sr-only">読み込み中</span>
      {Array.from({ length: rows }, (_, i) => (
        <div
          key={i}
          className="skeleton"
          style={{
            height,
            // 末尾を短くすると、文章の塊らしく見える。
            width: i === rows - 1 ? "60%" : "100%",
          }}
        />
      ))}
    </div>
  );
}

/**
 * ぼかしながら現れる。
 *
 * 本家（blur-fade）は motion の variants。ここは CSS アニメーションで、
 * 現れるときだけ効かせる。
 */
export function BlurFade({
  children,
  delay = 0,
  className,
}: {
  children: React.ReactNode;
  /** 秒。並べて出すときに少しずつずらす。 */
  delay?: number;
  className?: string;
}): React.JSX.Element {
  return (
    <div
      className={className === undefined ? "blur-fade" : `blur-fade ${className}`}
      style={delay === 0 ? undefined : { animationDelay: `${delay}s` }}
    >
      {children}
    </div>
  );
}

/**
 * 点の背景。
 *
 * 本家（dot-pattern）と同じく SVG の `pattern` で敷く。
 * 動かさない版だけを持つ——常時光っている背景は、
 * 業務画面では目障りになる。
 */
export function DotPattern({ gap = 16, radius = 1 }: { gap?: number; radius?: number }): React.JSX.Element {
  const id = useId();
  return (
    <svg aria-hidden="true" className="dot-pattern">
      <defs>
        <pattern id={id} width={gap} height={gap} patternUnits="userSpaceOnUse">
          <circle cx={radius} cy={radius} r={radius} />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill={`url(#${id})`} />
    </svg>
  );
}

/** 端末の設定で動きを減らしているか。 */
function prefersReducedMotion(): boolean {
  return globalThis.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;
}
